<template>
  <!-- Edit server: move a ProxySmart server (and the other record of the same box) onto a ProProxyUI box -->
  <div class="float-left w-full px-2 pb-2 modal-item">
    <div class="cv">
      <div class="cv-head"><b>Move this server to a ProProxyUI box</b></div>
      <p class="cv-dim">Customers keep the same HTTP and SOCKS5 ports, login, licence and rotation link. The host becomes the box's DNS name. Put the ProProxyUI box's address in Domain and its API token above.</p>

      <div class="cv-step">
        <span class="cv-n">1</span>
        <div class="cv-body">
          <div class="cv-line">
            <span>Port list from ProxySmart</span>
            <button type="button" class="cv-btn" :disabled="busy" @click="saveSnapshot">{{ st.snapshot_at ? 'Save again' : 'Save port list' }}</button>
          </div>
          <small v-if="st.snapshot_at" class="cv-ok">Saved {{ when(st.snapshot_at) }} · {{ st.ports }} ports on the ProxySmart box</small>
          <small v-else class="cv-warn">Not saved yet. Save it while ProxySmart is still online: it records which modem each port is on.</small>
        </div>
      </div>

      <div class="cv-step">
        <span class="cv-n">2</span>
        <div class="cv-body">
          <div class="cv-line">
            <span>Also move the other server on this box</span>
            <select v-model="pairUuid" class="cv-select" :disabled="!st.candidates || !st.candidates.length">
              <option value="">None</option>
              <option v-for="c in st.candidates || []" :key="c.uuid" :value="c.uuid">{{ c.name }} ({{ c.is_ipv6 ? 'IPv6' : 'IPv4' }}, {{ c.ports }} ports)</option>
            </select>
          </div>
          <small class="cv-dim">{{ st.candidates && st.candidates.length ? 'Both servers switch together. Ports on the IPv6 server are set to Dual (IPv6 first, IPv4 fallback).' : 'No other server has ports on this ProxySmart box.' }}</small>
        </div>
      </div>

      <div class="cv-step">
        <span class="cv-n">3</span>
        <div class="cv-body">
          <div class="cv-line">
            <span>Check every port against the new box</span>
            <button type="button" class="cv-btn" :disabled="busy || !st.snapshot_at || !domain || !token" @click="runPreview">Preview</button>
          </div>
          <template v-if="preview">
            <small class="cv-dim">Second port on the box: {{ offsetText(preview.offset) }}{{ preview.offset !== preview.box_offset ? ' (set when moving)' : ' (already set)' }}</small>
            <small v-for="g in preview.general" :key="g" class="cv-bad">{{ g }}</small>
            <div class="cv-table">
              <div class="cv-row cv-row--head"><span>Server</span><span>HTTP</span><span>SOCKS5</span><span>Modem</span><span>Login · limits</span><span>OS</span><span>IP</span><span>Licence</span><span>Check</span></div>
              <div v-for="l in preview.lines" :key="l.server + l.http_port" class="cv-row">
                <span>{{ l.server }}<i class="cv-tag" :class="l.ipv6 ? 'cv-tag--6' : 'cv-tag--4'">{{ l.ipv6 ? 'IPv6' : 'IPv4' }}</i></span>
                <span class="cv-mono">{{ l.http_port }}</span>
                <span class="cv-mono">{{ l.socks_port || '—' }}<small v-if="l.alt_port" class="cv-dim" style="display:block">own number</small></span>
                <span>{{ l.modem || (l.imei ? '…' + String(l.imei).slice(-4) : '—') }}<small class="cv-dim" style="display:block">{{ l.modem_state || (l.imei ? 'not on the box yet' : '') }}</small></span>
                <span class="cv-mono">{{ l.login || (l.ips ? 'IP ' + l.ips : '—') }}<small class="cv-dim" style="display:block">{{ limits(l) }}</small></span>
                <span>{{ l.os ? `${l.os_from} → ${l.os_name}` : '—' }}</span>
                <span>{{ l.ip_mode === 'dual' ? 'Dual' : 'IPv4' }}</span>
                <span>{{ l.licence ? (l.licence.email || '') : (l.sold ? 'sold' : 'for sale') }}<small v-if="l.licence" class="cv-dim" style="display:block">{{ String(l.licence.status || '').toLowerCase() }}</small></span>
                <span :class="l.problems.length ? 'cv-bad' : l.notes.length ? 'cv-note' : 'cv-ok'">{{ l.problems.length ? l.problems.join('; ') : l.notes.length ? l.notes.join('; ') : 'OK' }}</span>
              </div>
            </div>
            <small class="cv-dim">Box {{ preview.box_version ? 'v' + preview.box_version : '' }} · {{ preview.lines.length }} ports on {{ preview.servers.length }} server{{ preview.servers.length === 1 ? '' : 's' }} · {{ preview.issues ? preview.issues + ' with a problem (fix it, then Preview again)' : 'all ready' }}</small>
          </template>
        </div>
      </div>

      <div class="cv-step">
        <span class="cv-n">4</span>
        <div class="cv-body">
          <div class="cv-line">
            <span>Create the ports on the box and switch {{ pairUuid ? 'both servers' : 'the server' }} over</span>
            <button type="button" class="cv-btn cv-btn--pri" :disabled="busy || !preview || !preview.ok" @click="convert">{{ busy ? 'Working…' : (pairUuid ? 'Move 2 servers' : 'Move server') }}</button>
          </div>
          <small class="cv-dim">Can be undone here for 7 days (put the ProxySmart box back first).</small>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue';
import { fetchWrapper } from '@/helpers';
import { useAlertStore } from '@/stores';

const props = defineProps({ serverUuid: String, serverName: String, domain: String, token: String });
const emit = defineEmits(['done']);
const API = `${process.env.VUE_APP_VUE_API_URL}/admin/proproxyui/migrate`;
const alerts = useAlertStore();
const st = ref({});
const busy = ref(false);
const preview = ref(null);
const pairUuid = ref('');
const when = (v) => (v ? new Date(v).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const offsetText = (o) => (o ? `SOCKS5 = HTTP ${o > 0 ? '+' : '−'} ${Math.abs(o)}` : 'none');
const limits = (l) => [l.daily_gb ? `${l.daily_gb} GB/day (download)` : 'no daily limit', (l.limit_mbps || l.limit_up_mbps) ? `${l.limit_mbps || '∞'}/${l.limit_up_mbps || '∞'} Mbps` : '', l.max_conns ? `max ${l.max_conns}` : ''].filter(Boolean).join(' · ');

const load = async () => {
  if (!props.serverUuid) return;
  try {
    st.value = await fetchWrapper.post(`${API}/status`, { server_uuid: props.serverUuid });
    // the other record of the same box: preselect the one with the other IP version
    if (!pairUuid.value && st.value.candidates && st.value.candidates.length === 1) pairUuid.value = st.value.candidates[0].uuid;
  } catch (e) { st.value = {}; }
};
watch(() => props.serverUuid, () => { preview.value = null; pairUuid.value = ''; load(); }, { immediate: true });
watch(() => [props.domain, props.token, pairUuid.value], () => { preview.value = null; });

const saveSnapshot = async () => {
  busy.value = true;
  try { const r = await fetchWrapper.post(`${API}/snapshot`, { server_uuid: props.serverUuid }); alerts.success(`Port list saved: ${r.ports} ports on the ProxySmart box`); await load(); }
  catch (e) { alerts.error(e); }
  busy.value = false;
};
const runPreview = async () => {
  busy.value = true;
  try { preview.value = await fetchWrapper.post(`${API}/preview`, { server_uuid: props.serverUuid, domain: props.domain, token: props.token, pair_uuid: pairUuid.value || undefined }); }
  catch (e) { preview.value = null; alerts.error(e); }
  busy.value = false;
};
const convert = async () => {
  const names = preview.value.servers.map((s) => s.name).join(' and ');
  if (!confirm(`Move ${names} to the ProProxyUI box now? ${preview.value.lines.length} ports are created on the box and every licence moves with them.`)) return;
  busy.value = true;
  try {
    const r = await fetchWrapper.post(`${API}/convert`, { server_uuid: props.serverUuid, domain: props.domain, token: props.token, pair_uuid: pairUuid.value || undefined });
    alerts.success(`Moved: ${r.servers} server${r.servers === 1 ? '' : 's'}, ${r.ports} ports, ${r.licences} licences`);
    emit('done');
  } catch (e) { alerts.error(e); }
  busy.value = false;
};
</script>

<style scoped>
.cv { border: 1px solid var(--ui-line); border-radius: 10px; padding: 10px 12px; display: flex; flex-direction: column; gap: 10px; }
.cv-head { font-size: 15px; }
.cv-dim { color: var(--ui-dim); font-size: 12px; margin: 0; }
.cv-step { display: flex; gap: 10px; align-items: flex-start; }
.cv-n { flex: none; width: 22px; height: 22px; border-radius: 50%; border: 1px solid var(--ui-line-strong); display: inline-flex; align-items: center; justify-content: center; font-size: 12px; }
.cv-body { flex: 1; display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.cv-line { display: flex; align-items: center; justify-content: space-between; gap: 10px; font-size: 13px; flex-wrap: wrap; }
.cv-btn { padding: 3px 12px; border: 1px solid var(--ui-line-strong); border-radius: 6px; font-size: 12px; background: transparent; color: inherit; cursor: pointer; white-space: nowrap; }
.cv-btn:disabled { opacity: 0.5; cursor: default; }
.cv-btn--pri { background: var(--ui-pri); border-color: var(--ui-pri); color: #ffffff; }
.cv-select { padding: 3px 8px; border: 1px solid var(--ui-line-strong); border-radius: 6px; font-size: 12px; background: transparent; color: inherit; max-width: 260px; }
.cv-select option { color: #000; }
.cv-ok { color: #22c55e; font-size: 12px; }
.cv-warn { color: #f59e0b; font-size: 12px; }
.cv-bad { color: #ef4444; font-size: 12px; }
.cv-note { color: #f59e0b; font-size: 12px; }
.cv-table { display: flex; flex-direction: column; border: 1px solid var(--ui-line); border-radius: 8px; overflow-x: auto; margin-top: 4px; }
.cv-row { display: grid; grid-template-columns: 120px 52px 62px 80px minmax(140px, 1.3fr) 110px 44px minmax(110px, 1fr) minmax(120px, 1.1fr); gap: 8px; padding: 5px 8px; border-top: 1px solid var(--ui-line); font-size: 12px; min-width: 900px; align-items: start; }
.cv-row--head { border-top: 0; color: var(--ui-dim); }
.cv-mono { font-family: ui-monospace, Menlo, Consolas, monospace; }
.cv-tag { display: inline-block; font-style: normal; font-size: 10px; padding: 0 5px; margin-left: 4px; border-radius: 999px; }
.cv-tag--4 { background: rgba(59, 130, 246, 0.18); color: #60a5fa; }
.cv-tag--6 { background: rgba(168, 85, 247, 0.18); color: #c084fc; }
</style>
